import axios from 'axios'

import { type Organization } from '~/utils/types/user'

import { type ServerResponse } from './../axios'

export interface OrganizationListItem {
  id: number
  name: string
  description: string
  active: boolean
  logoUrlList: string[]
  supportEmail: null | string
  hasSubOrganizations: boolean
}

export async function listOrganizations({
  hubId,
  orgId,
  orgName,
  sortDirection,
}: {
  hubId: number | null
  orgId: number | null
  orgName: string | null
  sortDirection?: 'asc' | 'desc'
}) {
  const query = /* GraphQL */ `
    query ListOrganizations(
      $orgName: String
      $hubId: Long
      $orgId: Long
      $sortDirection: String
    ) {
      listOrganizations(
        filter: $orgName
        hubFilter: $hubId
        orgFilter: $orgId
        sortDirection: $sortDirection
      ) {
        id
        name
        description
        active
        logoUrlList
        supportEmail
      }
    }
  `

  return await axios<
    ServerResponse<{ listOrganizations: OrganizationListItem[] }>
  >({
    url: '/graphql',
    method: 'post',
    data: {
      query,
      variables: {
        hubId,
        orgId,
        orgName,
        sortDirection,
      },
    },
  })
}

export interface AllOrgsWithSubOrg {
  id: number
  name: string
}

export async function getOrganizationsWithSubOrgs(hubId: number) {
  const query = /* GraphQL */ `
    query AllOrgsWithSubOrgs($hubId: Long!) {
      allOrgsWithSubOrgs(hubFilter: $hubId) {
        id
        name
      }
    }
  `

  return await axios<
    ServerResponse<{ allOrgsWithSubOrgs: AllOrgsWithSubOrg[] }>
  >({
    method: 'post',
    url: '/graphql',
    data: { query, variables: { hubId } },
  })
}

export async function getOrganizationAreaTypes() {
  interface OrganizationAreaType {
    code: string
    description: string
  }

  return await axios<
    ServerResponse<{ organizationAreasTypes: OrganizationAreaType[] }>
  >({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query: 'query { organizationAreasTypes {code, description } }',
    }),
  })
}

export async function getOrganizationFinalUserTypes() {
  interface OrganizationFinalUserType {
    code: string
    description: string
  }

  return await axios<
    ServerResponse<{ organizationFinalUserTypes: OrganizationFinalUserType[] }>
  >({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query: 'query { organizationFinalUserTypes {code, description } }',
    }),
  })
}

export async function getOrganization(organizationId: number) {
  return await axios<Organization>({
    method: 'get',
    url: `/api/v2/organizations/${organizationId}`,
  })
}

export async function getOrganizationTypes() {
  interface OrganizationType {
    code: string
    description: string
  }

  return await axios<
    ServerResponse<{ organizantionTypes: OrganizationType[] }>
  >({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query: 'query { organizantionTypes {code, description } }',
    }),
  })
}

export async function organizationsByHub(hubId: number | null) {
  interface OrganizationsByHub {
    id: number
    name: string
  }
  const query = /* GraphQL */ `
    query OrganizationsByHub($hubId: Long) {
      organizations(hub: $hubId) {
        id
        name
      }
    }
  `

  return await axios<ServerResponse<{ organizations: OrganizationsByHub[] }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: { hubId },
    },
  })
}

export async function getOrganizationEndSurveyTypes() {
  interface OrganizationEndSurveyType {
    code: string
    description: string
  }

  return await axios<
    ServerResponse<{ organizationEndSurveyTypes: OrganizationEndSurveyType[] }>
  >({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query: 'query { organizationEndSurveyTypes {code, description } }',
    }),
  })
}
export interface OrganizationSubcategory {
  subcategory: string
  translatedSubCategory: string
}

export interface OrganizationCategory {
  category: string
  translatedCategory: string
  subcategories: OrganizationSubcategory[] | null
}

export async function getOrganizationCategories() {
  const query = /* GraphQL */ `
    query getCategoriesWithSubcategoriesForOrg {
      getCategoriesWithSubcategoriesForOrg {
        category
        translatedCategory
        subcategories {
          subcategory
          translatedSubCategory
        }
      }
    }
  `
  return await axios<
    ServerResponse<{
      getCategoriesWithSubcategoriesForOrg: OrganizationCategory[]
    }>
  >({
    method: 'post',
    url: '/graphql',
    data: { query },
  })
}

export async function getPurposesForCategoryAndSubcategory(category: string) {
  const query = `
    query getPurposesForCategoryAndSubcategory($category: String) {
      getPurposesForCategoryAndSubcategory(category: $category) { 
        purpose
        translatedPurpose
      } 
    }
  `
  return await axios<{
    data: {
      getPurposesForCategoryAndSubcategory: Array<{
        purpose: string
        translatedPurpose: string
      }>
    }
  }>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: { category },
    },
  })
}
